/**
 * Dynamically inject Meta fbq, TikTok ttq, and GTM dataLayer bootstrap
 * scripts. Called once on mount — idempotent (each snippet guards against
 * double-injection via the global it installs).
 */

import { ATTRIBUTION_HANDOFF_PARAM } from "@nextday/attribution";

import type { PixelConfig } from "@src/tracking/pixelConfig";

const SAFE_PIXEL_ID = /^[\w.-]+$/;
const SAFE_GA4_MEASUREMENT_ID = /^G-[A-Za-z0-9]+$/;

/** Strip any value that could break out of a JS string context. */
const sanitizeId = (raw: string): string | null => (SAFE_PIXEL_ID.test(raw) ? raw : null);

const sanitizeGa4MeasurementId = (raw: string): string | null => {
  const safe = sanitizeId(raw);
  if (!safe || !SAFE_GA4_MEASUREMENT_ID.test(safe)) return null;
  return safe;
};

const injectInline = (id: string, code: string): void => {
  if (document.getElementById(id)) return;
  const el = document.createElement("script");
  el.id = id;
  el.textContent = code;
  document.head.appendChild(el);
};

const injectExternal = (id: string, src: string): void => {
  if (document.getElementById(id)) return;
  const el = document.createElement("script");
  el.id = id;
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
};

export const injectMetaPixel = (pixelId: string): void => {
  const safe = sanitizeId(pixelId);
  if (!safe) return;
  injectInline(
    "nd-meta-pixel",
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){` +
      `n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};` +
      `if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];` +
      `t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];` +
      `s.parentNode.insertBefore(t,s)}(window,document,'script',` +
      `'https://connect.facebook.net/en_US/fbevents.js');` +
      `fbq('init','${safe}');`,
  );
};

export const injectTikTokPixel = (pixelId: string): void => {
  const safe = sanitizeId(pixelId);
  if (!safe) return;
  injectInline(
    "nd-tiktok-pixel",
    `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];` +
      `ttq.methods=["page","track","identify","instances","debug","on","off",` +
      `"once","ready","alias","group","enableCookie","disableCookie","holdConsent",` +
      `"revokeConsent","grantConsent"];ttq.setAndDefer=function(t,e){t[e]=function(){` +
      `t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;` +
      `i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=` +
      `function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)` +
      `ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r=` +
      `"https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;` +
      `ttq._i=ttq._i||{};ttq._i[e]=[];ttq._t=ttq._t||{};ttq._t[e]=+new Date;` +
      `ttq._o=ttq._o||{};ttq._o[e]=n||{};var script=d.createElement("script");` +
      `script.type="text/javascript";script.async=true;script.src=r+"?sdkid="+e+` +
      `"&lib="+t;var first=d.getElementsByTagName("script")[0];` +
      `first.parentNode.insertBefore(script,first)}}(window,document,"ttq");` +
      `ttq.load('${safe}');`,
  );
};

export const injectGtm = (containerId: string): void => {
  const safe = sanitizeId(containerId);
  if (!safe) return;
  injectInline(
    "nd-gtm-inline",
    `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),` +
      `event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),` +
      `dl=l!='dataLayer'?'&l='+l:'';j.async=true;` +
      `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;` +
      `f.parentNode.insertBefore(j,f)})(window,document,'script','dataLayer','${safe}');`,
  );
};

export const injectGtag = (measurementId: string, pageLocation?: string): void => {
  const safe = sanitizeGa4MeasurementId(measurementId);
  if (!safe) return;
  const config =
    pageLocation !== undefined && pageLocation !== ""
      ? `gtag('config','${safe}',{page_location:${JSON.stringify(pageLocation).replace(/</g, "\\u003c")}})`
      : `gtag('config','${safe}')`;
  injectInline(
    "nd-gtag-inline",
    `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}` +
      `gtag('js',new Date());${config}`,
  );
  injectExternal("nd-gtag-loader", `https://www.googletagmanager.com/gtag/js?id=${safe}`);
};

/**
 * Builds the GA4 `page_location` from the pre-strip landing URL: removes the
 * opaque `ad_h` envelope but keeps click ids and UTMs, which GA4 needs to
 * attribute the session. `funnel_id` is stamped onto the address bar after
 * boot, so it is carried over from `currentHref` when present. Returns
 * `undefined` for missing/invalid input so callers fall back to gtag's
 * default page_location.
 */
export const buildGa4PageLocation = (
  landingHref: string | undefined,
  currentHref?: string,
): string | undefined => {
  if (landingHref === undefined || landingHref === "") return undefined;
  try {
    const url = new URL(landingHref);
    url.searchParams.delete(ATTRIBUTION_HANDOFF_PARAM);
    if (currentHref !== undefined && currentHref !== "") {
      const funnelId = new URL(currentHref).searchParams.get("funnel_id");
      if (funnelId !== null && funnelId !== "") {
        url.searchParams.set("funnel_id", funnelId);
      }
    }
    return url.toString();
  } catch {
    return undefined;
  }
};

export const injectPixelScripts = (config: PixelConfig): void => {
  if (config.metaPixelId) injectMetaPixel(config.metaPixelId);
  if (config.tiktokPixelId) injectTikTokPixel(config.tiktokPixelId);
  if (config.gtmContainerId) injectGtm(config.gtmContainerId);
};
